import { useTranslation } from 'react-i18next';
import { distanceLabel } from '../../utils/geo';
import { TRAVEL_MODES } from '../../utils/routing/travelMode';
import { externalDestination, googleMapsUrl, wazeUrl } from '../../utils/routing/externalNav';

/*
 * The way to the target, under the national map's target banner: how the
 * person travels (4x2 + walk, 4x4 + walk, walk), what the route is (drive so
 * far, walk so far, the last steps off any path), and the paved drive handed
 * to Waze or Google Maps.
 *
 * `state` is utils/routing/useRoute's. When there is no route -- still being
 * found, none in the data, no data on the device -- it says so, and the map
 * keeps the straight line.
 */
export function durationLabel(seconds, t) {
  const minutes = Math.max(1, Math.round((Number(seconds) || 0) / 60));
  if (minutes < 60) return t('route:duration.min', { n: minutes });
  return t('route:duration.hm', { h: Math.floor(minutes / 60), m: minutes % 60 });
}

export function ModeSwitch({ mode, onChange, short = false, testid = 'route-mode', style }) {
  const { t } = useTranslation('route');
  return (
    <div role="group" aria-label={t('modeLabel')} data-testid={testid}
      style={{ display: 'inline-flex', border: '1px solid var(--c-border, rgba(255,255,255,.5))', borderRadius: 8,
        overflow: 'hidden', ...style }}>
      {TRAVEL_MODES.map((m) => (
        <button key={m} type="button" aria-pressed={mode === m} title={t(`tip.${m}`)}
          data-testid={`${testid}-${m}`} onClick={() => onChange(m)}
          style={{ border: 'none', padding: short ? '6px 8px' : '8px 12px', minHeight: 40, cursor: 'pointer',
            font: 'inherit', fontSize: short ? 12 : 14, fontWeight: mode === m ? 700 : 500,
            background: mode === m ? 'var(--c-primary, #2e7d32)' : 'transparent',
            color: mode === m ? '#fff' : 'inherit' }}>
          {t(short ? `modesShort.${m}` : `modes.${m}`)}
        </button>
      ))}
    </div>
  );
}

export function routeSummary(route, t) {
  if (!route) return '';
  const parts = [];
  if (route.drive) {
    parts.push(t('route:summary.drive', { distance: distanceLabel(route.drive.metres / 1000, t),
      time: durationLabel(route.drive.seconds, t) }));
  }
  if (route.walk) {
    const key = route.drive ? 'route:summary.walk' : 'route:summary.walkOnly';
    parts.push(t(key, { distance: distanceLabel(route.walk.metres / 1000, t), time: durationLabel(route.walk.seconds, t) }));
  }
  if (route.offTrail) parts.push(t('route:summary.offTrail', { distance: distanceLabel(route.offTrail.metres / 1000, t) }));
  return parts.join(' · ');
}

export default function RoutePanel({ state, mode, onMode }) {
  const { t } = useTranslation(['route', 'common']);
  const route = state?.status === 'ok' ? state.route : null;
  const destination = externalDestination(route);
  return (
    <div data-testid="route-panel" data-status={state?.status || 'idle'}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <ModeSwitch mode={mode} onChange={onMode} />
      {route && (
        <p style={{ margin: 0, fontSize: 14 }} data-testid="route-summary"><bdi>{routeSummary(route, t)}</bdi></p>
      )}
      {route?.dropOff && <p style={{ margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('park')}</p>}
      {!route && state?.status && state.status !== 'idle' && (
        <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }} data-testid="route-status">
          {t(`status.${state.status}`, { defaultValue: t('status.error') })}
        </p>
      )}
      {destination && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-testid="route-external">
          <span style={{ fontSize: 13 }}>{t('external.label')}</span>
          <a href={wazeUrl(destination)} target="_blank" rel="noopener noreferrer" title={t('tip.waze')}
            data-testid="route-waze" style={extLink}>{t('external.waze')}</a>
          <a href={googleMapsUrl(destination)} target="_blank" rel="noopener noreferrer" title={t('tip.google')}
            data-testid="route-google" style={extLink}>{t('external.google')}</a>
          <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', flexBasis: '100%' }}>
            {t(route.mode === 'car4' ? 'external.car4' : 'external.car2')}
          </span>
        </div>
      )}
      {route && <p style={{ margin: 0, fontSize: 11, color: 'var(--c-text-muted-fg)' }}>{t('attribution')}</p>}
    </div>
  );
}

const extLink = {
  display: 'inline-flex', alignItems: 'center', minHeight: 36, padding: '0 12px', borderRadius: 6,
  border: '1px solid var(--c-border)', textDecoration: 'none', color: 'var(--c-text-fg)', fontSize: 14,
  background: 'var(--c-surface)',
};
